<template>
  <wizard
    first-step="personalDetails"
    class="wizard-cancel-contract"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="selectChildren"
      :model="formData"
      :next-step="submitSelectedChildren"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.selectChildren.title }}</h2>
        <slot name="select-children-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.labels.childRegistrationItems }}</h3>

        <FormulateInput
          type="group"
          name="children"
          :help="i18n.form.help.childRegistrationForm"
          :add-label="i18n.form.addlabel.childRegistrationForm"
          :repeatable="true"
          validation="bail|min:1,length|max:5,length|unique:childRegistrationForm"
          :validation-messages="i18n.form.validations.childRegistrationItems"
          @repeatableRemoved="resetChangesPerChild"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->
            <FormulateInput
              type="text"
              name="name"
              :label="i18n.form.labels.childName"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.childName"
            />
          </div>
        </FormulateInput>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="cancelContract"
      :model="formData"
      next-step="reasonOfTermination"
      previous-step="selectChildren"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.cancelContract.title }}</h2>
        <slot name="cancel-contract-paragraph" />
      </wizard-step-intro>

      <fields-date-picker
        :i18n="i18n"
        :min-date="minDateForChange"
        :date-validation="'bail|required|after:' + dateBeforeMinDateForChange"
      />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="reasonOfTermination"
      :model="formData"
      next-step="remark"
      previous-step="cancelContract"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.reasonOfTermination.title }}</h2>
        <slot name="reason-of-termination-paragraph" />
      </wizard-step-intro>

      <fields-reason-of-termination
        :i18n="i18n"
        :model="formData.reasonOfTermination"
      />
      <fields-reason-of-termination-quality
        v-if="showCancellationReasonQualityFields"
        :i18n="i18n"
        :model="formData.reasonOfTermination"
      />
      <fields-reason-of-termination-situation
        v-if="showCancellationReasonSituationFields"
        :i18n="i18n"
        :model="formData.reasonOfTermination"
      />
      <fields-reason-of-termination-cost
        v-if="showCancellationReasonCostFields"
        :i18n="i18n"
        :model="formData.reasonOfTermination"
      />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="remark"
      :model="formData"
      next-step="review"
      previous-step="reasonOfTermination"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.remark.title }}</h2>
        <slot name="remark-paragraph" />
      </wizard-step-intro>
      <fields-remark
        :show-contact-request="false"
        :i18n="i18n"
        :model="formData.remark"
      />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="remark"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <td>{{ i18n.form.labels.reviewName }}</td>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewEmail }}</td>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewPhoneNumber }}</td>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewCustomerNumber }}</td>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>
          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewContractCancellationDate }}</td>
            <td>{{ moment(resultingData.cancelContract.date).format('DD-MM-YYYY') }}</td>
          </tr>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
import FieldsReasonOfTermination from '@/components/forms/fields/FieldsReasonOfTermination.vue';
import FieldsReasonOfTerminationCost from '@/components/forms/fields/FieldsReasonOfTerminationCost.vue';
import FieldsReasonOfTerminationQuality from '@/components/forms/fields/FieldsReasonOfTerminationQuality.vue';
import FieldsReasonOfTerminationSituation from '@/components/forms/fields/FieldsReasonOfTerminationSituation.vue';
import FieldsRemark from '@/components/forms/fields/FieldsRemark.vue';
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import getQueryParams from '@/lib/formulate/getFormQueryParam';
import utils from '@/lib/utils';
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'WizardCancelContract',
  components: {
    FieldsDatePicker,
    FieldsReasonOfTermination,
    FieldsReasonOfTerminationQuality,
    FieldsReasonOfTerminationSituation,
    FieldsReasonOfTerminationCost,
    FieldsRemark,
    Wizard,
    WizardStep,
    WizardStepIntro,
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => { },
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    minDateForChange: {
      type: String,
      default: () => null,
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
  },
  data() {
    return {
      formData: {
        personalDetails: this.$store.state.wizard.personalDetails,
        selectChildren: this.$store.state.wizard.selectedChildren,
        cancelContract: {},
        reasonOfTermination: {},
        remark: {},
      },
      request: {
        isSending: false,
        errorSending: false,
      },
    };
  },
  computed: {
    moment() {
      return moment;
    },
    dateBeforeMinDateForChange() {
      return utils.date.getDate(new Date(this.minDateForChange), -1);
    },
    showCancellationReasonQualityFields() {
      return this.formData.reasonOfTermination?.reasonofterminationselected === 'quality';
    },
    showCancellationReasonSituationFields() {
      return this.formData.reasonOfTermination?.reasonofterminationselected === 'situation';
    },
    showCancellationReasonCostFields() {
      return this.formData.reasonOfTermination?.reasonofterminationselected === 'cost';
    },
    resultingData() {
      return {
        label: this.label,
        locale: this.locale,
        personalDetails: this.formData.personalDetails,
        changesPerChild: this.formData.selectChildren.children
          .map((child) => ({
            childName: child.name,
          })),
        cancelContract: this.formData.cancelContract,
        reasonOfTermination: this.formData.reasonOfTermination,
        remarkContact: this.formData.remark,
      };
    },
  },
  created () {
    this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }
  },
  methods: {
    submitPersonalDetails() {
      this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
      return 'selectChildren';
    },
    submitSelectedChildren() {
      this.$store.dispatch('wizard/setSelectedChildren', this.formData.selectChildren);
      return 'cancelContract';
    },
    resetChangesPerChild() {
      Object.keys(this.formData)
        .filter((k) => k.startsWith('changesForChild-'))
        .forEach((k) => (this.formData[k] = {}));
    },
    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      axios
        .post('/umbraco/api/wizard/cancelcontract', this.resultingData, config)
        .then(() => {
          window.location.href = this.successPageUrl;
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
  },
};
</script>
